import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../Modal';
import { deskiesApi } from './deskiesApi';
import { HSL } from './colors';

function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  busy,
  confirmLabel,
}) {
  return (
    <Modal modalOpen={open} onClose={onClose} title={title} small>
      <div>
        <Typography variant="body2" sx={{ color: HSL.textStrong, mb: 3 }}>
          {message}
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={busy}
            sx={{
              color: HSL.textStrong,
              borderColor: HSL.border,
              textTransform: 'none',
              '&:hover': {
                borderColor: HSL.textStrong,
                backgroundColor: HSL.neutralSoft,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            variant="contained"
            disabled={busy}
            sx={{
              backgroundColor: HSL.primary,
              textTransform: 'none',
              '&:hover': { backgroundColor: 'hsl(342, 71%, 38%)' },
            }}
          >
            {busy ? (
              <CircularProgress size={18} sx={{ color: HSL.surface }} />
            ) : (
              confirmLabel || 'Confirm'
            )}
          </Button>
        </Stack>
      </div>
    </Modal>
  );
}

function PreviewModal({ open, onClose, preview, loading }) {
  return (
    <Modal modalOpen={open} onClose={onClose} title="Deskie email preview">
      <div style={{ minHeight: 360 }}>
        {loading && (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 6 }}>
            <CircularProgress sx={{ color: HSL.primary }} size={28} />
            <Typography variant="body2" sx={{ color: HSL.textMuted }}>
              Rendering a random deskie pairing from this cohort&apos;s draft…
            </Typography>
          </Stack>
        )}
        {!loading && preview && (
          <>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              sx={{
                mb: 2,
                p: 1.5,
                backgroundColor: HSL.neutralSoft,
                borderRadius: '6px',
                border: `1px solid ${HSL.border}`,
              }}
            >
              <Typography variant="caption" sx={{ color: HSL.textMuted }}>
                <strong style={{ color: HSL.textStrong }}>To:</strong>{' '}
                {preview.sample_recipient_name} &lt;
                {preview.sample_recipient_email}&gt;
              </Typography>
              <Typography variant="caption" sx={{ color: HSL.textMuted }}>
                <strong style={{ color: HSL.textStrong }}>Subject:</strong>{' '}
                {preview.subject}
              </Typography>
              <Typography variant="caption" sx={{ color: HSL.textMuted }}>
                <strong style={{ color: HSL.textStrong }}>Peer Group:</strong> #
                {preview.peer_group_number}
              </Typography>
            </Stack>
            <iframe
              title="Deskie email preview"
              srcDoc={preview.html}
              style={{
                width: '100%',
                height: '70vh',
                border: `1px solid ${HSL.border}`,
                borderRadius: '8px',
                backgroundColor: '#fff',
              }}
            />
          </>
        )}
      </div>
    </Modal>
  );
}

function DeskieActions({ cohortId, status }) {
  const queryClient = useQueryClient();
  const [confirmRegenOpen, setConfirmRegenOpen] = useState(false);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const invalidate = () => {
    queryClient.invalidateQueries(['deskies', cohortId]);
  };

  const regenerateMutation = useMutation(
    () => deskiesApi.regenerate(cohortId),
    {
      onSuccess: () => {
        toast.success('Deskies regeneration started');
        setConfirmRegenOpen(false);
        invalidate();
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.error || 'Failed to regenerate deskies'
        );
      },
    }
  );

  const holdMutation = useMutation(() => deskiesApi.hold(cohortId), {
    onSuccess: () => {
      toast.success('Publication held');
      invalidate();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to hold publication');
    },
  });

  const resumeMutation = useMutation(() => deskiesApi.resume(cohortId), {
    onSuccess: () => {
      toast.success('Publication resumed');
      invalidate();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.error || 'Failed to resume publication'
      );
    },
  });

  const publishMutation = useMutation(() => deskiesApi.publish(cohortId), {
    onSuccess: (res) => {
      const queued = res?.data?.emails_queued ?? 0;
      toast.success(`Queued ${queued} deskie emails for delivery`);
      setConfirmSendOpen(false);
      invalidate();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.error || 'Failed to send deskie emails'
      );
    },
  });

  const previewMutation = useMutation(() => deskiesApi.preview(cohortId), {
    onSuccess: (data) => {
      setPreviewData(data);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.error || 'Failed to render preview');
      setPreviewOpen(false);
    },
  });

  const openPreview = () => {
    setPreviewData(null);
    setPreviewOpen(true);
    previewMutation.mutate();
  };

  if (status !== 'draft' && status !== 'held') {
    return null;
  }

  return (
    <>
      <Stack direction="row" spacing={1.5} flexWrap="wrap">
        <Button
          variant="outlined"
          startIcon={<VisibilityIcon />}
          onClick={openPreview}
          disabled={previewMutation.isLoading}
          sx={{
            borderColor: HSL.secondary,
            color: HSL.secondary,
            textTransform: 'none',
            '&:hover': {
              borderColor: HSL.secondary,
              backgroundColor: HSL.secondarySoft,
            },
          }}
        >
          Preview email
        </Button>

        {status === 'draft' && (
          <>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => setConfirmRegenOpen(true)}
              disabled={regenerateMutation.isLoading}
              sx={{
                borderColor: HSL.primary,
                color: HSL.primary,
                textTransform: 'none',
                '&:hover': {
                  borderColor: HSL.primary,
                  backgroundColor: HSL.primarySoft,
                },
              }}
            >
              Regenerate
            </Button>
            <Button
              variant="outlined"
              startIcon={<PauseCircleOutlineIcon />}
              onClick={() => holdMutation.mutate()}
              disabled={holdMutation.isLoading}
              sx={{
                borderColor: HSL.warning,
                color: HSL.warning,
                textTransform: 'none',
                '&:hover': {
                  borderColor: HSL.warning,
                  backgroundColor: HSL.warningSoft,
                },
              }}
            >
              Hold
            </Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={() => setConfirmSendOpen(true)}
              disabled={publishMutation.isLoading}
              sx={{
                backgroundColor: HSL.primary,
                textTransform: 'none',
                '&:hover': { backgroundColor: 'hsl(342, 71%, 38%)' },
              }}
            >
              Send emails
            </Button>
          </>
        )}
        {status === 'held' && (
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={() => resumeMutation.mutate()}
            disabled={resumeMutation.isLoading}
            sx={{
              backgroundColor: HSL.primary,
              textTransform: 'none',
              '&:hover': { backgroundColor: 'hsl(342, 71%, 38%)' },
            }}
          >
            Resume
          </Button>
        )}
      </Stack>

      <ConfirmDialog
        open={confirmRegenOpen}
        onClose={() => setConfirmRegenOpen(false)}
        onConfirm={() => regenerateMutation.mutate()}
        title="Regenerate deskies?"
        message="Are you sure? This wipes the current draft and runs the matcher again."
        busy={regenerateMutation.isLoading}
      />

      <ConfirmDialog
        open={confirmSendOpen}
        onClose={() => setConfirmSendOpen(false)}
        onConfirm={() => publishMutation.mutate()}
        title="Send deskie emails?"
        message="This will queue one email per learner in this cohort's current draft. Make sure you've previewed the email first — this cannot be undone."
        busy={publishMutation.isLoading}
        confirmLabel="Send emails"
      />

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        preview={previewData}
        loading={previewMutation.isLoading}
      />
    </>
  );
}

export default DeskieActions;
